import queryString from "querystring";
import {
  protectedDelete,
  protectedDeleteWithBody,
  protectedGet,
  protectedPost,
  protectedPut,
} from "./restUtil";
import { getSessionUser, isUserStudent } from "./authUtil";
import { addItemToRecentStudents } from "./students";
import { fetchExercisesByTag } from "./tags";
import { fetchExercisesBySource } from "./content";
import { getUserFullName } from "./util";

export const DEFAULT_UNSUPPORTED_CONTENT_MESSAGE =
  "Unfortunately we don\u2019t support this exercise.";

class ExerciseType {
  constructor(name, displayName, defaultDescription) {
    this.name = name;
    this.displayName = displayName;
    this.defaultDescription = defaultDescription;
  }
}

export class ExerciseTypes {
  static MULTI_CHOICES = new ExerciseType(
    "multiple-choice",
    "Multiple Choices",
    "Select the correct choice for the question below."
  );
  static MISSING_WORDS = new ExerciseType(
    "missing-words",
    "Missing Words",
    "Select the most appropriate choice for each box..."
  );
  static FREE_FORM = new ExerciseType(
    "free-form",
    "Free Form",
    "Enter the answer in the area below."
  );
  static READ_CONTENT = new ExerciseType(
    "read-content",
    "Read the content",
    "In this exercise you need to read the specified library content."
  );

  static getAllTypes = () => {
    return [
      ExerciseTypes.MISSING_WORDS,
      ExerciseTypes.FREE_FORM,
      ExerciseTypes.READ_CONTENT,
    ];
  };

  static isReadingExercise = type => type === ExerciseTypes.READ_CONTENT.name;
}

export function getExerciseLabel({ exercise, libraryObject }) {
  /**
   * Returns exercise's name by default.
   * 'Read: {libraryObject.name}' if the type of the exercise is 'read-content'.
   */
  if (ExerciseTypes.isReadingExercise(exercise.type) && libraryObject) {
    return `Read: ${libraryObject.name}`;
  } else {
    return exercise.name;
  }
}

export function getReadingExercise(libraryObject) {
  /**
   * Returns true if the specified library object has a reading exercise.
   */
  const exercises = libraryObject.exercises.filter(ex =>
    ExerciseTypes.isReadingExercise(ex.type)
  );
  if (exercises.length === 1) {
    return exercises[0];
  } else {
    return null;
  }
}

export function getExerciseTypeClass(type) {
  switch (type) {
    case ExerciseTypes.MISSING_WORDS.name:
      return ExerciseTypes.MISSING_WORDS;
    case ExerciseTypes.FREE_FORM.name:
      return ExerciseTypes.FREE_FORM;
    case ExerciseTypes.MULTI_CHOICES.name:
      return ExerciseTypes.MULTI_CHOICES;
    case ExerciseTypes.READ_CONTENT.name:
      return ExerciseTypes.READ_CONTENT;
    default:
      return {};
  }
}

export function fetchExerciseDetails(args, onSuccess, onError) {
  return protectedGet(
    "/exercise/" + args.id,
    undefined,
    exerciseResponse => {
      if (exerciseResponse && exerciseResponse.id && isUserStudent()) {
        return protectedGet(
          "/exercise/assignments",
          {
            exercise_id: exerciseResponse.id,
          },
          assignmentResponse => {
            if (!assignmentResponse || !assignmentResponse.list) {
              return onError && onError("invalid response structure");
            }
            exerciseResponse.assignment =
              assignmentResponse.list.length === 1 ? assignmentResponse.list[0] : null;
            return onSuccess && onSuccess(exerciseResponse);
          },
          onError
        );
      } else {
        return onSuccess && onSuccess(exerciseResponse);
      }
    },
    onError
  );
}

export function fetchExerciseWithContent(args, onSuccess, onError) {
  return protectedGet(
    "/exercise/" + args.id,
    undefined,
    resp => {
      if (!resp || !resp.id) {
        return onError && onError("invalid response structure");
      }
      fetchExerciseContent(
        {
          id: resp.id,
        },
        resp2 => {
          resp.xml = resp2;
          return onSuccess && onSuccess(resp);
        },
        onError
      );
    },
    onError
  );
}

export function searchExercises(args, onSuccess, onError) {
  const queryStringObj = {};
  if (args.search) {
    queryStringObj.contains = args.search;
  }
  if (args.student_id) {
    queryStringObj.exclude_student_assignments = args.student_id;
  }
  if (args.creator_id) {
    queryStringObj.creator_id = args.creator_id;
  }
  if (args.level_id) {
    queryStringObj.level_id = args.level_id;
  }
  if (args.s3_prefix) {
    queryStringObj.s3_prefix = args.s3_prefix;
  }
  if (args.page) {
    queryStringObj.page = args.page;
  }
  if (args.size) {
    queryStringObj.size = args.size;
  }
  return protectedGet(
    "/exercises",
    queryString.stringify(queryStringObj),
    onSuccess,
    onError
  );
}

export function searchExercisesByText(search) {
  return function _searchExercisesByText(args, onSuccess, onError) {
    args.search = search;
    searchExercises(args, onSuccess, onError);
  };
}

export function fetchExercisesFilteredByLevel(levelId) {
  return function _fetchExercisesFilteredByLevel(args, onSuccess, onError) {
    args.level_id = levelId;
    searchExercises(args, onSuccess, onError);
  };
}

export function fetchExercisesFilteredByTag(tagId) {
  return function _fetchExercisesFilteredByTag(args, onSuccess, onError) {
    args.id = tagId;
    fetchExercisesByTag(args, onSuccess, onError);
  };
}

export function fetchExercisesFilteredBySource(sourceId) {
  return function _fetchExercisesFilteredBySource(args, onSuccess, onError) {
    args.id = sourceId;
    fetchExercisesBySource(args, onSuccess, onError);
  };
}

export function fetchExercisesFilteredByPathPrefix(s3Prefix) {
  return function _fetchExercisesFilteredByPathPrefix(args, onSuccess, onError) {
    args.s3_prefix = s3Prefix;
    searchExercises(args, onSuccess, onError);
  };
}

export function fetchReadingAssignmentsByAssigner(assignerId, isSubmitted) {
  return function _fetchReadingAssignmentsByAssigner(args, onSuccess, onError) {
    if (assignerId !== null) {
      args.assigner_id = assignerId;
    }
    if (isSubmitted !== null) {
      args.submitted = isSubmitted;
    }
    args.exercise_type = ExerciseTypes.READ_CONTENT.name;
    protectedGet("/exercise/assignments", args, onSuccess, onError);
  };
}

export function fetchExerciseAssignments(args, onSuccess, onError) {
  return protectedGet(
    "/exercise/assignments",
    args,
    resp => {
      if (!resp || !resp.list) {
        return onError && onError("invalid response structure");
      }
      return onSuccess && onSuccess(resp.list);
    },
    onError
  );
}

export function fetchUnfinishedExerciseAssignments(args, onSuccess, onError) {
  args.submitted = false;
  return protectedGet("/exercise/assignments", args, onSuccess, onError);
}

export function fetchRecentlyCompletedExerciseAssignments(args, onSuccess, onError) {
  args.submitted = true;
  return protectedGet(
    "/exercise/assignments",
    args,
    resp => {
      if (!resp || !resp.list) {
        return onError && onError("invalid response structure");
      }
      return onSuccess && onSuccess(resp.list);
    },
    onError
  );
}

export function fetchCompletedExerciseAssignments(args, onSuccess, onError) {
  args.submitted = true;
  return protectedGet("/exercise/assignments", args, onSuccess, onError);
}

export function fetchExerciseAssignmentsAssignedByStudent(args, onSuccess, onError) {
  args.assigner_id = getSessionUser().id;
  return protectedGet("/exercise/assignments", args, onSuccess, onError);
}

export function fetchExerciseAssignmentDetails(args, onSuccess, onError) {
  return protectedGet("/exercise/assignment/" + args.id, undefined, onSuccess, onError);
}

export function fetchMyLearningsExerciseAssignments(args, onSuccess, onError) {
  args.exclude_types = ExerciseTypes.READ_CONTENT.name;
  return protectedGet("/exercise/assignments", args, onSuccess, onError);
}

export function fetchContent(url, onSuccess, onError) {
  return protectedGet(
    url,
    undefined,
    resp => {
      if (!resp || !resp.type || resp.type.indexOf("xml") === -1) {
        return onError && onError(DEFAULT_UNSUPPORTED_CONTENT_MESSAGE);
      }
      return onSuccess && onSuccess(resp.xhr.responseXML);
    },
    onError,
    {
      // Thanks to this additional parameter, we will receive the whole response
      // with headers and content type, not only body.
      returnResponse: true,
    }
  );
}

export function fetchExerciseContent(args, onSuccess, onError) {
  return fetchContent("/exercise/" + args.id + "/content", onSuccess, onError);
}

export function removeExercise(args, onSuccess, onError) {
  protectedDelete("/exercise/" + args.id, undefined, onSuccess, onError);
}

export function fetchExerciseAssignmentContent(args, onSuccess, onError) {
  return fetchContent("/exercise/assignment/" + args.id + "/content", onSuccess, onError);
}

/**
 Special function for chained exercises:
 Downloads current assignment content and most recent chained exercise assignment and replaces current assignment's
 answer (presumably empty) with answer from chained assignment.
 */
export function fetchChainedExerciseAssignmentContent(
  args,
  onSuccess,
  onError,
  hasAssignment = true
) {
  const fetchChainedContent = hasAssignment
    ? fetchExerciseAssignmentContent
    : fetchExerciseContent;
  return fetchChainedContent(
    args,
    assignment => {
      if (assignment.getElementsByTagName("answer").length !== 1) {
        return onSuccess(assignment);
      }
      return protectedGet(
        "/exercise/assignments",
        {
          exercise_id: args.chained_exercise_id,
          page: 1,
          size: 1,
          submitted: true,
        },
        resp => {
          if (!resp || !resp.list || resp.list.length !== 1) {
            return onSuccess(assignment);
          }
          // load the assignment content.
          const chainedAssignmentId = resp.list[0].id;
          return fetchExerciseAssignmentContent(
            { id: chainedAssignmentId },
            previousAssignment => {
              const previousAnswers = previousAssignment.getElementsByTagName("answer");
              if (previousAnswers.length !== 1) {
                return onSuccess(assignment);
              }
              const answers = assignment.getElementsByTagName("answer");
              if (answers.length !== 1) {
                return onSuccess(assignment);
              }
              const answer = answers[0];
              answer.parentElement.replaceChild(previousAnswers[0], answer);
              onSuccess(assignment);
            },
            onError
          );
        },
        onError
      );
    },
    onError
  );
}

export function submitAnswer(args, onSuccess, onError) {
  return protectedPut(
    `/exercise/assignment/${args.id}/answer`,
    args.answer,
    onSuccess,
    onError,
    {
      contentType: "application/xml",
    }
  );
}

export function rateFeedback(args, onSuccess, onError) {
  return protectedPost(
    `/exercise/assignment/${args.id}/feedback/rating`,
    {
      rating: args.rating,
    },
    onSuccess,
    onError
  );
}

export function testAnswer(args, onSuccess, onError) {
  return protectedPost("/exercise/test", args.answer, onSuccess, onError, {
    contentType: "application/xml",
    accept: "application/xml",
    returnResponse: true,
  });
}

export function assignExercises(args, onSuccess, onError) {
  if (!isUserStudent()) {
    addItemToRecentStudents(args.student);
  }

  let payload = {
    student_id: args.student.id,
    exercises: args.exercise_ids,
  };

  if (args.library_object_id !== undefined) {
    payload.library_object_id = args.library_object_id || null;
  }

  if (args.session_id) {
    payload.session_id = args.session_id;
  }

  return protectedPost("/exercise/assignments", payload, onSuccess, errs => {
    const firstErr = errs.errors?.[0];
    if (
      (firstErr?.description || "").includes("existing assignment") &&
      firstErr?.location === "body" &&
      firstErr?.name === "exercise"
    ) {
      let studenName = getUserFullName(args.student?.profile || args.student);
      errs.errors[0].description =
        args.library_object_id !== undefined
          ? `You have already assigned this lesson to ${studenName}`
          : `Exercise has already been assigned to ${studenName}`;
    }

    onError(errs);
  });
}

export function removeExerciseAssignments(args, onSuccess, onError) {
  return protectedDeleteWithBody(
    "/exercise/assignments",
    {
      exercise_assignments: args.exercise_assignments,
    },
    onSuccess,
    onError
  );
}

export function changeExerciseAssignments(args, onSuccess, onError) {
  const promises = [];
  if (args.exercise_assignments.toAdd.length > 0) {
    promises.push(
      new Promise((resolve, reject) => {
        return assignExercises(
          {
            student: args.student,
            session_id: args.session_id,
            exercise_ids: args.exercise_assignments.toAdd,
          },
          resolve,
          reject
        );
      })
    );
  }
  if (args.exercise_assignments.toDelete.length > 0) {
    promises.push(
      new Promise((resolve, reject) => {
        return removeExerciseAssignments(
          {
            exercise_assignments: args.exercise_assignments.toDelete,
          },
          resolve,
          reject
        );
      })
    );
  }
  return Promise.all(promises).then(onSuccess).catch(onError);
}

export function createAssignmentByStudent(args, onSuccess, onError) {
  return protectedPost(
    "/exercise/assignments",
    {
      student_id: args.student_id,
      library_object_id: args.library_object_id,
      exercises: [args.exercise_id],
    },
    onSuccess,
    onError
  );
}

export function fetchAssignmentsByLesson(args, onSuccess, onError) {
  return protectedGet(
    "/exercise/assignments",
    {
      session_id: args.session_id,
    },
    resp => {
      if (!resp || !resp.list) {
        return onError && onError("invalid response structure");
      }
      return onSuccess && onSuccess(resp.list);
    },
    onError
  );
}

export function fetchAssignmentsByStudent(args, onSuccess, onError) {
  return protectedGet(
    "/exercise/assignments",
    {
      student_id: args.student_id,
      page: args.page,
      size: args.size,
    },
    resp => {
      if (!resp || !resp.list) {
        return onError && onError("invalid response structure");
      }
      return onSuccess && onSuccess(resp);
    },
    onError
  );
}

export function fetchAssignmentsByLibraryObject(args, onSuccess, onError) {
  // If this function is used by teacher, then we need student_id as well!
  return protectedGet(
    "/exercise/assignments",
    {
      library_object_id: args.library_object_id,
    },
    resp => {
      if (!resp || !resp.list) {
        return onError && onError("invalid response structure");
      }
      return onSuccess && onSuccess(resp);
    },
    onError
  );
}

export function fetchMyLearningsRecentLibraryObjects(args, onSuccess, onError) {
  return protectedGet("/library/recent", args, onSuccess, onError);
}

export function fetchMyLearningsFavoriteLibraryObjects(args, onSuccess, onError) {
  return protectedGet("/library/favorite", args, onSuccess, onError);
}

export function fetchMyLearningsCompletedReadingList(args, onSuccess, onError) {
  return protectedGet("/library/completed", args, onSuccess, onError);
}

export function getLibraryObjectTags(args, onSuccess, onError) {
  protectedGet(
    "/library/" + args.library_object_id + "/posindex/tags",
    undefined,
    onSuccess,
    onError
  );
}

export function getWordList(args, onSuccess, onError) {
  protectedGet(
    "/library/" + args.library_object_id + "/posindex/tag/" + args.tag + "/tokens",
    undefined,
    onSuccess,
    onError
  );
}

export function fetchCompletedLibrarySections(id, onSuccess, onError) {
  return protectedGet(
    `/library/${id}/sections`,
    {
      "data-plugin-type": "section",
    },
    onSuccess,
    onError
  );
}

export function completeLibrarySection(args, onSuccess, onError) {
  return protectedPost(
    `/library/${args.id}/sections`,
    {
      section_id: args.section_id,
    },
    onSuccess,
    onError
  );
}

export function generateExercise(args, onSuccess, onError) {
  protectedPost(
    "/exercise/abracadabra",
    {
      type: ExerciseTypes.MISSING_WORDS.name /* only this type for now */,
      tag: args.tag,
      words: args.words,
      library_object_id: args.library_object_id,
      s3_prefix: args.s3_prefix,
      name: args.name,
      description: args.description,
    },
    onSuccess,
    onError
  );
}

export function commonUploadExercise(baseURL, args, onSuccess, onError) {
  let url = baseURL;
  const queryStringObj = {};
  if (args.exercise_id) {
    queryStringObj.exercise_id = args.exercise_id;
  }
  if (args.s3_prefix) {
    queryStringObj.s3_prefix = args.s3_prefix;
  }
  const queryStr = queryString.stringify(queryStringObj);
  if (queryStr) {
    url += "?" + queryStr;
  }

  protectedPost(url, args.xml, onSuccess, onError, {
    contentType: "application/xml",
  });
}

export function uploadExercise(args, onSuccess, onError) {
  commonUploadExercise(
    "/exercise/upload",
    args,
    resp => {
      if (
        (args.tagsToAdd && args.tagsToAdd.length > 0) ||
        (args.tagsToDelete && args.tagsToDelete.length > 0)
      ) {
        const promises = [];
        if (args.tagsToAdd.length > 0) {
          promises.push(
            new Promise((resolve, reject) => {
              addTagToExercise(
                {
                  id: resp.id,
                  tags: args.tagsToAdd,
                },
                resolve,
                reject
              );
            })
          );
        }

        if (args.tagsToDelete.length > 0) {
          promises.push(
            new Promise((resolve, reject) => {
              deleteTagFromExercise(
                {
                  id: resp.id,
                  tags: args.tagsToDelete,
                },
                resolve,
                reject
              );
            })
          );
        }

        Promise.all(promises).then(onSuccess, onError);
      } else {
        onSuccess(resp);
      }
    },
    onError
  );
}

export function getExerciseLibraryObject(exercise) {
  // Returns a library object associated with the exercise. It's possible to have a lot of them.
  // Returns one and only element or null if there are more than one element.
  if (exercise.library_objects && exercise.library_objects.length === 1) {
    return exercise.library_objects[0];
  } else {
    return null;
  }
}

export function addTagToExercise(args, onSuccess, onError) {
  protectedPost(
    "/exercise/" + args.id + "/tags",
    {
      tags: args.tags,
    },
    onSuccess,
    onError
  );
}

export function deleteTagFromExercise(args, onSuccess, onError) {
  protectedDeleteWithBody(
    "/exercise/" + args.id + "/tags",
    {
      tags: args.tags,
    },
    onSuccess,
    onError
  );
}

export function translateExerciseFeedback(args, onSuccess, onError) {
  return protectedGet(
    `/exercise/assignment/${args.id}/feedback/translate?lang=${args.lang}`,
    undefined,
    onSuccess,
    onError,
    {
      returnResponse: true,
    }
  );
}
